import { useEffect, useId, useRef, useState } from "react";
import { Loader2Icon } from "lucide-react";
import { handleError } from "@/utils/error";
import { Button, Input, V2Label, cn } from "web-design-system";
import { useMinorGuardian, useMinorGuardianKycCheck } from "creso-core/hooks";
import { panSchema } from "creso-core/zod";
import { isGuardianKycValidated, titleCase } from "creso-core/utils";
import { Client } from "creso-core/types";
import { KycStatusBadge } from "./kyc-status";

/**
 * Checks the guardian's KYC, which links them to the minor's account. The
 * account's PAN is the minor's own, or the guardian's when they have none.
 */
export function MinorKycFields({
  client,
  onVerifiedChange,
  disabled,
}: {
  client: Client | null | undefined;
  onVerifiedChange: (isVerified: boolean) => void;
  disabled?: boolean;
}) {
  const guardianPanId = useId();
  const minorPanId = useId();
  const [guardianPan, setGuardianPan] = useState("");
  const [minorPan, setMinorPan] = useState("");
  const [showMinorPan, setShowMinorPan] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkedMinorPan, setCheckedMinorPan] = useState("");
  const { check, isChecking, result } = useMinorGuardianKycCheck(client);
  const { guardian, isLoading: isLoadingGuardian } = useMinorGuardian(client);
  const prefilled = useRef(false);

  // Fill in the PANs the lead already has: the linked guardian's (else the
  // account's, which is the guardian's when the minor has none), and the
  // account's as the minor's own when it differs from the guardian's
  useEffect(() => {
    if (prefilled.current || !client?.id || isLoadingGuardian) return;
    prefilled.current = true;
    const savedGuardianPan = guardian?.pan || client.pan || "";
    setGuardianPan((prev) => prev || savedGuardianPan);
    if (guardian?.pan && client.pan && client.pan !== guardian.pan) {
      setMinorPan((prev) => prev || client.pan || "");
      setShowMinorPan(true);
    }
  }, [client, guardian, isLoadingGuardian]);

  // A result only counts for the PANs it was checked against
  const checked =
    result?.pan === guardianPan && checkedMinorPan === minorPan ? result : null;
  // Registered-only KYC is treated like no KYC
  const isVerified = isGuardianKycValidated(
    checked?.kycStatus,
    checked?.kycStatusRemark
  );
  const guardianName = titleCase(checked?.name ?? "");

  useEffect(() => {
    onVerifiedChange(isVerified);
  }, [isVerified, onVerifiedChange]);

  const handleCheck = async () => {
    const parsed = panSchema.safeParse(guardianPan);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid PAN");
      return;
    }
    if (minorPan && !panSchema.safeParse(minorPan).success) {
      setError("Enter a valid minor PAN");
      return;
    }
    setError(null);
    try {
      // Checking a different PAN replaces the guardian linked by the last check
      await check({
        guardianPan,
        minorPan,
        replace: !!result && result.pan !== guardianPan,
      });
      setCheckedMinorPan(minorPan);
    } catch (e) {
      handleError(e, "Failed to check KYC status");
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <V2Label htmlFor={guardianPanId}>Guardian PAN</V2Label>
        <div className="flex flex-row items-start gap-2">
          <Input
            id={guardianPanId}
            value={guardianPan}
            onChange={(e) => {
              setGuardianPan(e.target.value.toLocaleUpperCase());
              setError(null);
            }}
            disabled={disabled || isChecking}
            maxLength={10}
            placeholder="ABCDE1234F"
            className="flex-1"
          />
          <div className="shrink-0">
            {checked ? (
              <KycStatusBadge
                kyc_status={checked.kycStatus}
                remark={checked.kycStatusRemark}
              />
            ) : (
              <Button
                variant="outline"
                type="button"
                disabled={!guardianPan || isChecking || disabled}
                onClick={handleCheck}
              >
                <Loader2Icon
                  className={cn("size-4 animate-spin", {
                    hidden: !isChecking,
                  })}
                />
                Check KYC
              </Button>
            )}
          </div>
        </div>
        {!!error && <span className="text-xs text-destructive">{error}</span>}
        {isVerified && (
          <div className="rounded-lg p-3 border border-border text-xs">
            KYC Validated for{" "}
            <span className="text-xs font-semibold">{guardianName}</span>
            {checked?.guardianClient && (
              <span className="text-xs text-muted-foreground">
                {" "}
                · already your client
              </span>
            )}
          </div>
        )}
        {checked && !isVerified && (
          <div className="rounded-lg p-3 text-xs bg-tag-orange text-tag-yellow-foreground border border-tag-yellow-foreground">
            {checked.kycStatus === "S"
              ? "KYC not validated for this PAN"
              : "No KYC found for this PAN"}
          </div>
        )}
      </div>

      {showMinorPan ? (
        <div className="flex flex-col gap-2">
          <V2Label htmlFor={minorPanId}>
            Minor PAN&nbsp;
            <span className="text-muted-foreground">(Optional)</span>
          </V2Label>
          <Input
            id={minorPanId}
            value={minorPan}
            onChange={(e) => setMinorPan(e.target.value.toLocaleUpperCase())}
            disabled={disabled}
            maxLength={10}
            placeholder="ABCDE1234F"
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowMinorPan(true)}
          className="self-start text-sm font-medium underline"
        >
          Minor has own PAN? Add it
        </button>
      )}
    </div>
  );
}
